import React from 'react';
import { Stack, TextField, IconButton, InputLabel, Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

type ProcessorConfigItem = {
  processorId: string;
  label: string;
  brandCode: string;
};

export type ValueFormat = Record<string, { processorId: string; label: string; brandCode: string }>;

interface SettingProcessorConfigFieldProps {
  value: ValueFormat;
  onChange: (e: { target: { value: object } }) => void;
  label?: string;
}

function toItems(value: ValueFormat): ProcessorConfigItem[] {
  if (!value) return [];
  return (
    Object.entries(value).map(([processorId, v]) => {
      const obj = v;
      return {
        processorId: processorId as string,
        label: obj?.label ?? '',
        brandCode: obj?.brandCode ?? '',
      };
    }) ?? []
  );
}

function toOutput(items: ProcessorConfigItem[]): object {
  return Object.fromEntries(
    items.map((i) => [
      i.processorId,
      { processor: i.processorId, label: i.label, brandCode: i.brandCode },
    ]),
  );
}

const SettingProcessorConfigField: React.FC<SettingProcessorConfigFieldProps> = ({
  value,
  onChange,
  label = 'Processor MID config',
}) => {
  const items = toItems(value);

  const updateItems = (newItems: ProcessorConfigItem[]) => {
    const output = { target: { value: toOutput(newItems) } };
    onChange(output);
  };

  const handleAction = (
    action: 'add' | 'remove' | 'change',
    args?: { index: number; field?: keyof ProcessorConfigItem; val?: string },
  ) => {
    switch (action) {
      case 'remove':
        updateItems(items.filter((_, i) => i !== args?.index));
        break;
      case 'add':
        updateItems([...items, { processorId: '', label: '', brandCode: '' }]);
        break;
      case 'change':
        if (args?.field) {
          const newItems = [...items];
          newItems[args.index] = { ...newItems[args.index], [args.field]: args.val };
          updateItems(newItems);
        }
        break;
    }
  };

  return (
    <Stack spacing={2}>
      <InputLabel
        sx={{
          minWidth: { xs: 0, sm: 130 },
          color: 'text.primary',
          fontWeight: 500,
        }}>
        {label}
      </InputLabel>
      <Stack spacing={1.5}>
        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              p: 1.5,
              borderRadius: 1,
              bgcolor: '#f7f7f7',
              border: '1px solid',
              borderColor: 'divider',
            }}>
            <TextField
              size="small"
              label="Processor MID"
              value={item.processorId}
              onChange={(e) =>
                handleAction('change', { index, field: 'processorId', val: e.target.value })
              }
              sx={{ flex: '1 1 300px', minWidth: 120 }}
              placeholder="e.g. uuid or id"
            />
            <TextField
              size="small"
              label="Label"
              value={item.label}
              onChange={(e) =>
                handleAction('change', { index, field: 'label', val: e.target.value })
              }
              sx={{ flex: '1 1 150px', minWidth: 120 }}
              placeholder="e.g. Stripe KG PH2"
            />

            <TextField
              size="small"
              label="Brand"
              value={item.brandCode}
              onChange={(e) =>
                handleAction('change', { index, field: 'brandCode', val: e.target.value })
              }
              sx={{ flex: '0 1 150px', minWidth: 80 }}
              placeholder="e.g. CF, BM"
            />

            <IconButton
              color="error"
              onClick={() => handleAction('remove', { index })}
              size="small"
              sx={{ flexShrink: 0 }}
              aria-label="Remove">
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        ))}
      </Stack>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => handleAction('add')}
        sx={{ alignSelf: 'flex-start' }}>
        Add
      </Button>
    </Stack>
  );
};

export default SettingProcessorConfigField;
